import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  ConnectHarnessCredentialRequest,
  ConnectHarnessCredentialResponse,
  HarnessCredential,
  HarnessCredentialProvider,
  HarnessCredentialsResponse,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  HARNESS_CREDENTIALS: {
    LIST: "/api/v2/org/:organization_id/harness-credentials",
    CONNECT: "/api/v2/org/:organization_id/harness-credentials/:provider",
    REVOKE:
      "/api/v2/org/:organization_id/harness-credentials/:harness_credential_id",
    USER_LIST: "/api/v2/org/:organization_id/user/:user_id/harness-credentials",
    USER_CONNECT:
      "/api/v2/org/:organization_id/user/:user_id/harness-credentials/:provider",
    USER_REVOKE:
      "/api/v2/org/:organization_id/user/:user_id/harness-credentials/:harness_credential_id",
  },
};

export class HarnessCredentialsAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  // Organization Harness Credential Methods

  /**
   * Get the organization's connected credentials (org scope only).
   */
  async list(config?: AxiosRequestConfig): Promise<HarnessCredential[]> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.get<HarnessCredentialsResponse>(
      route(ENDPOINTS.HARNESS_CREDENTIALS.LIST, {
        organization_id: orgId || "",
      }),
      config,
    );

    return response.data.data.harness_credentials;
  }

  /**
   * Connect an org-scoped credential, replacing whatever the organization had for this provider.
   */
  async connect(
    provider: HarnessCredentialProvider,
    data: ConnectHarnessCredentialRequest,
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential> {
    const orgId = await this.client.getOrgID();

    const response =
      await this.client.axios.post<ConnectHarnessCredentialResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.CONNECT, {
          organization_id: orgId || "",
          provider,
        }),
        data,
        config,
      );

    return response.data.data.harness_credential;
  }

  /**
   * Revoke an org-scoped credential.
   */
  async revoke(
    harnessCredentialId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.HARNESS_CREDENTIALS.REVOKE, {
        organization_id: orgId || "",
        harness_credential_id: harnessCredentialId,
      }),
      config,
    );
  }

  // User Harness Credential Methods

  /**
   * Get what the current user's harness turns resolve to: their own credentials plus the
   * organization's, which they inherit for any provider they have not connected themselves.
   */
  async listUserHarnessCredentials(
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<HarnessCredentialsResponse>(
      route(ENDPOINTS.HARNESS_CREDENTIALS.USER_LIST, {
        organization_id: orgId || "",
        user_id: userId || "",
      }),
      config,
    );

    return response.data.data.harness_credentials;
  }

  /**
   * Connect a credential for the current user, taking precedence over the organization's.
   */
  async connectUserHarnessCredential(
    provider: HarnessCredentialProvider,
    data: ConnectHarnessCredentialRequest,
    config?: AxiosRequestConfig,
  ): Promise<HarnessCredential> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.post<ConnectHarnessCredentialResponse>(
        route(ENDPOINTS.HARNESS_CREDENTIALS.USER_CONNECT, {
          organization_id: orgId || "",
          user_id: userId || "",
          provider,
        }),
        data,
        config,
      );

    return response.data.data.harness_credential;
  }

  /**
   * Revoke one of the current user's credentials. Their turns fall back to the organization's.
   */
  async revokeUserHarnessCredential(
    harnessCredentialId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.HARNESS_CREDENTIALS.USER_REVOKE, {
        organization_id: orgId || "",
        user_id: userId || "",
        harness_credential_id: harnessCredentialId,
      }),
      config,
    );
  }
}
